import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Checkbox, Input, Modal, ModalFooter } from '../ui';

const saveSearchFormSchema = z.object({
  name: z.string().trim().min(2, 'Le nom doit contenir au moins 2 caractères.').max(80),
  isShared: z.boolean(),
});

type SaveSearchFormValues = z.infer<typeof saveSearchFormSchema>;

interface SaveSearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: SaveSearchFormValues) => Promise<void>;
  initialName?: string;
  initialIsShared?: boolean;
  isUpdate?: boolean;
  filterCount: number;
  visibleColumnCount: number;
}

export function SaveSearchDialog({
  isOpen,
  onClose,
  onSubmit,
  initialName = '',
  initialIsShared = false,
  isUpdate = false,
  filterCount,
  visibleColumnCount,
}: SaveSearchDialogProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SaveSearchFormValues>({
    resolver: zodResolver(saveSearchFormSchema),
    defaultValues: { name: initialName, isShared: initialIsShared },
  });

  useEffect(() => {
    if (!isOpen) return;
    reset({ name: initialName, isShared: initialIsShared });
    setSubmitError(null);
  }, [initialIsShared, initialName, isOpen, reset]);

  async function submit(values: SaveSearchFormValues) {
    setSubmitError(null);
    try {
      await onSubmit(values);
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setSubmitError(
        message.includes('duplicate') || message.includes('unique') || message.includes('23505')
          ? 'Une recherche porte déjà ce nom pour cette liste.'
          : 'Impossible d’enregistrer la recherche. Réessayez.'
      );
    }
  }

  const isShared = watch('isShared');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isUpdate ? 'Mettre à jour la recherche' : 'Enregistrer la recherche'}
      footer={
        <ModalFooter
          onCancel={onClose}
          formId="save-search-form"
          submitLabel={isUpdate ? 'Mettre à jour' : 'Enregistrer'}
          isSubmitting={isSubmitting}
        />
      }
    >
      <form id="save-search-form" onSubmit={handleSubmit(submit)} className="space-y-5">
        <div className="space-y-2">
          <label htmlFor="saved-search-name" className="text-sm font-medium text-zinc-900">
            Nom de la recherche
          </label>
          <Input id="saved-search-name" autoFocus {...register('name')} />
          {errors.name && <p className="text-sm text-red-600">{errors.name.message}</p>}
        </div>

        <Checkbox
          checked={isShared}
          onChange={(checked) => setValue('isShared', checked, { shouldDirty: true })}
          label="Partager avec les autres utilisateurs"
        />

        <div className="p-3 bg-zinc-50 text-sm text-zinc-600 rounded-md">
          Cette vue conservera {filterCount} filtre{filterCount > 1 ? 's' : ''} et{' '}
          {visibleColumnCount} colonne{visibleColumnCount > 1 ? 's' : ''} visible
          {visibleColumnCount > 1 ? 's' : ''}.
        </div>

        {submitError && <p className="text-sm text-red-600">{submitError}</p>}
      </form>
    </Modal>
  );
}
